/**
 * `useExpandState(docId, blockId)` — per-doc-per-block-per-reader
 * expand/collapse state for the wiki's expand-section NodeView. Persists
 * across reloads via `localStorage` keyed `wiki:expand:{docId}:{blockId}`.
 *
 * Why local storage instead of the doc body? An expand block has TWO
 * orthogonal pieces of state:
 *
 *   1. The `summary` attribute — what label sits next to the chevron.
 *      That's author-controlled, lives in the TipTap JSON, and is shared
 *      across every reader of the doc.
 *
 *   2. The open / closed state — what a SPECIFIC reader is looking at
 *      right now. That's per-reader. Persisting it to the doc would mean
 *      every reader's clicks fight every other reader's clicks; with
 *      multiple tabs open by the same user, they'd fight themselves.
 *
 *   Notion + Confluence both keep this in localStorage for the same
 *   reason — matches the user's mental model of "this collapsed thing
 *   stays collapsed for me until I open it."
 *
 * The hook is a `useState` mirror of the localStorage value:
 *
 *   - Initial render reads localStorage once (lazy initializer).
 *   - Every `setIsOpen` call writes back to localStorage AND updates state.
 *   - Missing / unparseable / SSR-no-window cases all collapse to
 *     "closed" — Notion's default for new collapse blocks.
 *
 * SSR / non-browser safety: `typeof window === 'undefined'` early-bails
 * on both the read + write paths. The hook is safe to import from any
 * file that might be touched by a unit test running in node-without-DOM.
 *
 * The `blockId` argument should be a stable UUID generated at insert
 * time (the slash command attaches it via `attrs: { blockId: crypto.randomUUID() }`).
 * NEVER use `addAttributes().default` to generate it — defaults run once
 * per schema and give every expand the same id.
 */

import { useCallback, useState } from 'react'

/**
 * Build the localStorage key for a given doc + block. Exported so the
 * unit test suite can write fixture values without re-implementing the
 * naming convention.
 */
export function expandStateKey(docId: string, blockId: string): string {
  return `wiki:expand:${docId}:${blockId}`
}

function readInitial(docId: string, blockId: string): boolean {
  if (typeof window === 'undefined') return false
  try {
    const raw = window.localStorage.getItem(expandStateKey(docId, blockId))
    if (raw === '1' || raw === 'true') return true
    return false
  } catch {
    // QuotaExceededError on read is impossible but `localStorage`
    // throws when the surrounding context disallows storage (e.g. some
    // private-browsing modes); collapse to closed in that case.
    return false
  }
}

export function useExpandState(docId: string, blockId: string): [boolean, (next: boolean) => void] {
  const [isOpen, setIsOpenState] = useState<boolean>(() => readInitial(docId, blockId))

  const setIsOpen = useCallback(
    (next: boolean) => {
      setIsOpenState(next)
      if (typeof window === 'undefined') return
      try {
        const key = expandStateKey(docId, blockId)
        if (next) {
          window.localStorage.setItem(key, '1')
        } else {
          // Closed is the default; remove the key entirely so we don't
          // accumulate cruft when readers collapse-then-leave.
          window.localStorage.removeItem(key)
        }
      } catch {
        // Same defensive shape as the reader — private-browsing mode
        // can throw on `setItem`. We've already updated the in-memory
        // state, so the UI behaves correctly for this session; we just
        // don't persist.
      }
    },
    [docId, blockId],
  )

  return [isOpen, setIsOpen]
}
